import React, { useState } from 'react';
import {
  Layout, Button, Space, Tag, Modal, Input, Form,
  message, Empty, Typography, Row, Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  TagsOutlined,
} from '@ant-design/icons';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

// ══════════════════════════════════════════════
//  Mock 数据：标签分类和标签值
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

const INITIAL_CATEGORIES = [
  {
    key: 'serviceContent',
    name: '服务内容',
    tags: [
      { name: '设备SDK对接', color: '#1677ff' },
      { name: 'AppSDK对接', color: '#1677ff' },
      { name: '平台API对接', color: '#1677ff' },
      { name: 'WEB SDK对接', color: '#1677ff' },
      { name: 'App OEM定制', color: '#1677ff' },
      { name: '云平台私有化部署', color: '#1677ff' },
      { name: '行业SaaS定制', color: '#1677ff' },
    ],
  },
  {
    key: 'customerCategory',
    name: '客户分类',
    tags: [
      { name: '公版定制', color: '#52c41a' },
      { name: '标准对接', color: '#52c41a' },
      { name: '私有定制', color: '#52c41a' },
      { name: '维护型', color: '#52c41a' },
    ],
  },
  {
    key: 'capability',
    name: '能力标签',
    tags: [
      { name: '工厂', color: '#fa8c16' },
      { name: '方案商', color: '#fa8c16' },
      { name: '渠道商', color: '#fa8c16' },
      { name: '应用服务商', color: '#fa8c16' },
    ],
  },
  {
    key: 'customerTag',
    name: '客户标签',
    tags: [
      { name: '01战略客户', color: '#eb2f96' },
      { name: '02大客户', color: '#eb2f96' },
      { name: '03中等客户', color: '#eb2f96' },
    ],
  },
];

// ══════════════════════════════════════════════
//  子组件：新增/编辑分类弹窗
// ══════════════════════════════════════════════

const CategoryModal = ({ visible, editRecord, onCancel, onSave }) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (visible && editRecord) {
      form.setFieldsValue({ name: editRecord.name });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, editRecord, form]);

  return (
    <Modal
      title={editRecord ? '重命名分类' : '新增分类'}
      open={visible}
      onCancel={() => { form.resetFields(); onCancel(); }}
      onOk={() => {
        form.validateFields().then((values) => {
          onSave?.(values.name);
          form.resetFields();
        }).catch(() => {});
      }}
      width={400}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
          <Input placeholder="如：服务内容" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ══════════════════════════════════════════════
//  子组件：新增/编辑标签值弹窗
// ══════════════════════════════════════════════

const TagValueModal = ({ visible, editRecord, categoryColor, onCancel, onSave }) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (visible && editRecord) {
      form.setFieldsValue({ name: editRecord.name, color: editRecord.color });
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({ color: categoryColor || '#1677ff' });
    }
  }, [visible, editRecord, form, categoryColor]);

  return (
    <Modal
      title={editRecord ? '编辑标签' : '新增标签'}
      open={visible}
      onCancel={() => { form.resetFields(); onCancel(); }}
      onOk={() => {
        form.validateFields().then((values) => {
          onSave?.(values);
          form.resetFields();
        }).catch(() => {});
      }}
      width={400}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="标签名称" rules={[{ required: true, message: '请输入标签名称' }]}>
          <Input placeholder="如：设备SDK对接" />
        </Form.Item>
        <Form.Item name="color" label="标签颜色">
          <Space size={8} wrap>
            {['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d', '#faad14'].map((c) => (
              <div
                key={c}
                onClick={() => form.setFieldValue('color', c)}
                style={{
                  width: 32, height: 32, borderRadius: 6, background: c, cursor: 'pointer',
                  border: form.getFieldValue('color') === c ? '3px solid #000' : '2px solid transparent',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ══════════════════════════════════════════════
//  主组件：TagManage
// ══════════════════════════════════════════════

const TagManage = () => {
  const [categories, setCategories] = useState(INITIAL_CATEGORIES);
  const [selectedKey, setSelectedKey] = useState(INITIAL_CATEGORIES[0]?.key);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryEditRecord, setCategoryEditRecord] = useState(null);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [tagEditRecord, setTagEditRecord] = useState(null);

  const selectedCategory = categories.find((c) => c.key === selectedKey);

  // 分类操作
  const handleAddCategory = () => {
    setCategoryEditRecord(null);
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (cat) => {
    setCategoryEditRecord(cat);
    setCategoryModalVisible(true);
  };

  const handleDeleteCategory = (cat) => {
    Modal.warning({
      title: '无法删除',
      content: `「${cat.name}」下有标签被 N 个企业使用，请先解绑后再删除。`,
    });
  };

  const handleCategorySave = (name) => {
    if (categoryEditRecord) {
      setCategories((prev) => prev.map((c) => (c.key === categoryEditRecord.key ? { ...c, name } : c)));
    } else {
      const newKey = `cat_${Date.now()}`;
      setCategories((prev) => [...prev, { key: newKey, name, tags: [] }]);
      setSelectedKey(newKey);
    }
    setCategoryModalVisible(false);
    setCategoryEditRecord(null);
    message.success(categoryEditRecord ? '分类已重命名' : '分类已创建');
  };

  // 标签操作
  const handleAddTag = () => {
    setTagEditRecord(null);
    setTagModalVisible(true);
  };

  const handleEditTag = (tag) => {
    setTagEditRecord(tag);
    setTagModalVisible(true);
  };

  const handleDeleteTag = (tag) => {
    Modal.warning({
      title: '无法删除',
      content: `「${tag.name}」被 N 个企业使用，请先解绑后再删除。`,
    });
  };

  const handleTagSave = (values) => {
    if (tagEditRecord) {
      setCategories((prev) => prev.map((cat) => {
        if (cat.key !== selectedKey) return cat;
        return {
          ...cat,
          tags: cat.tags.map((t) => (t.name === tagEditRecord.name ? { ...t, name: values.name, color: values.color } : t)),
        };
      }));
    } else {
      setCategories((prev) => prev.map((cat) => {
        if (cat.key !== selectedKey) return cat;
        return { ...cat, tags: [...cat.tags, { name: values.name, color: values.color }] };
      }));
    }
    setTagModalVisible(false);
    setTagEditRecord(null);
    message.success(tagEditRecord ? '标签已更新' : '标签已创建');
  };

  return (
    <Layout style={{ minHeight: 'calc(100vh - 64px)', background: '#fff' }}>
      {/* 左侧分类列表 */}
      <Sider width={220} style={{ background: '#fff', borderRight: '1px solid #f0f0f0', padding: '16px 0' }}>
        <div style={{ padding: '0 16px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: 14 }}>标签分类</Text>
          <Button type="link" size="small" icon={<PlusOutlined />} onClick={handleAddCategory} />
        </div>
        {categories.length === 0 ? (
          <Empty description="暂无标签分类" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            {categories.map((cat) => (
              <div
                key={cat.key}
                onClick={() => setSelectedKey(cat.key)}
                style={{
                  padding: '10px 16px', cursor: 'pointer',
                  background: selectedKey === cat.key ? '#e6f4ff' : 'transparent',
                  borderRight: selectedKey === cat.key ? '3px solid #1677ff' : '3px solid transparent',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: selectedKey === cat.key ? 600 : 400 }}>{cat.name}</span>
                <Space size={4} onClick={(e) => e.stopPropagation()}>
                  <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditCategory(cat)} />
                  <Button type="link" size="small" icon={<DeleteOutlined />} danger onClick={() => handleDeleteCategory(cat)} />
                </Space>
              </div>
            ))}
          </>
        )}
      </Sider>

      {/* 右侧标签值 */}
      <Content style={{ padding: '16px 24px', overflow: 'auto' }}>
        {selectedCategory ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <Title level={5} style={{ margin: 0 }}>
                <TagsOutlined style={{ marginRight: 8 }} />
                {selectedCategory.name}
              </Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTag}>新增标签</Button>
            </div>
            {selectedCategory.tags.length === 0 ? (
              <Empty description="该分类下暂无标签，点击「新增标签」开始" />
            ) : (
              <Row gutter={[12, 12]}>
                {selectedCategory.tags.map((tag) => (
                  <Col key={tag.name}>
                    <Tag
                      color={tag.color}
                      closable
                      onClose={(e) => { e.preventDefault(); handleDeleteTag(tag); }}
                      onClick={() => handleEditTag(tag)}
                      style={{
                        fontSize: 14, padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                        border: 'none', margin: 0,
                      }}
                    >
                      {tag.name}
                    </Tag>
                  </Col>
                ))}
              </Row>
            )}
          </>
        ) : (
          <Empty description="请选择左侧标签分类" />
        )}
      </Content>

      {/* 弹窗 */}
      <CategoryModal
        visible={categoryModalVisible}
        editRecord={categoryEditRecord}
        onCancel={() => { setCategoryModalVisible(false); setCategoryEditRecord(null); }}
        onSave={handleCategorySave}
      />
      <TagValueModal
        visible={tagModalVisible}
        editRecord={tagEditRecord}
        categoryColor={selectedCategory?.tags?.[0]?.color}
        onCancel={() => { setTagModalVisible(false); setTagEditRecord(null); }}
        onSave={handleTagSave}
      />
    </Layout>
  );
};

export default TagManage;
